import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  UserCog,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  AlertCircle,
  Shield,
  Check,
  Lock,
} from "lucide-react";
import { rolesApi } from "../../services/api";
import { PERMISSION_LABELS } from "../onboarding/steps/InviteTeamStep";
import Toggle from "../ui/Toggle";

// Map permission key names to short labels for the backend string-based permissions
const PERMISSION_KEYS = PERMISSION_LABELS.map((p) => p.key);

const RoleManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Create / Edit form
  const [editing, setEditing] = useState<any | null>(null); // null = create mode, object = editing
  const [showForm, setShowForm] = useState(false);
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formPermissions, setFormPermissions] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Delete
  const [deleting, setDeleting] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const fetchRoles = useCallback(async () => {
    try {
      const res = await rolesApi.list();
      setRoles(res.data || []);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await fetchRoles();
      setLoading(false);
    };
    load();
  }, [fetchRoles]);

  const openCreate = () => {
    setEditing(null);
    setFormName("");
    setFormDescription("");
    setFormPermissions([]);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (role: any) => {
    setEditing(role);
    setFormName(role.name);
    setFormDescription(role.description || "");
    setFormPermissions(role.permissions || []);
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setFormError(null);
  };

  const togglePermission = (perm: string) => {
    setFormPermissions((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      setFormError("Role name is required.");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        name: formName.trim(),
        description: formDescription.trim() || null,
        permissions: formPermissions,
      };
      if (editing) {
        await rolesApi.update(editing._id, payload);
      } else {
        await rolesApi.create(payload);
      }
      closeForm();
      await fetchRoles();
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to save role.";
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try {
      await rolesApi.remove(id);
      setConfirmDelete(null);
      await fetchRoles();
    } catch (err: any) {
      console.error("Delete role failed:", err);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/settings")}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm font-medium">Back to Settings</span>
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Role Management</h1>
            <p className="text-sm text-slate-500 mt-1">
              Define roles and permissions for your team
            </p>
          </div>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand text-white rounded-xl text-sm font-medium hover:bg-brand-hover transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Role
          </button>
        </div>
      </div>

      {/* Create / Edit Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden mb-6"
          >
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-slate-800">
                  {editing ? "Edit Role" : "Create New Role"}
                </h3>
                <button
                  onClick={closeForm}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Role Name</label>
                  <input
                    type="text"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="e.g. Senior Associate"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1.5">Description</label>
                  <input
                    type="text"
                    value={formDescription}
                    onChange={(e) => setFormDescription(e.target.value)}
                    placeholder="Brief description of this role"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all text-sm"
                  />
                </div>
              </div>

              {/* Permissions Grid */}
              <div className="mb-4">
                <label className="block text-xs font-medium text-slate-500 mb-2">Permissions</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {PERMISSION_LABELS.map(({ key, label, description, icon: PermIcon }) => {
                    const enabled = formPermissions.includes(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => togglePermission(key)}
                        className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${
                          enabled
                            ? "bg-brand-light border-brand-light"
                            : "bg-white border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        <div className="mt-0.5 flex-shrink-0">
                          <Toggle checked={enabled} onChange={() => {}} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <PermIcon
                              className={`h-3.5 w-3.5 flex-shrink-0 ${
                                enabled ? "text-brand" : "text-slate-400"
                              }`}
                            />
                            <span
                              className={`text-sm font-medium ${
                                enabled ? "text-brand" : "text-slate-600"
                              }`}
                            >
                              {label}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5 leading-tight">{description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {formError && (
                <div className="flex items-center gap-2 bg-red-50 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{formError}</span>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  onClick={handleSave}
                  disabled={saving || !formName.trim()}
                  className="flex items-center gap-2 px-5 py-2.5 bg-brand text-white rounded-xl text-sm font-medium hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="h-4 w-4" />
                      {editing ? "Update Role" : "Create Role"}
                    </>
                  )}
                </button>
                <button
                  onClick={closeForm}
                  className="px-4 py-2.5 text-sm font-medium text-slate-600 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Roles List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
        </div>
      ) : roles.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <UserCog className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 mb-4">No roles defined yet</p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand text-white rounded-xl text-sm font-medium hover:bg-brand-hover transition-colors"
          >
            <Plus className="h-4 w-4" />
            Create Your First Role
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {roles.map((role, i) => {
            const permCount = role.permissions?.length || 0;
            return (
              <motion.div
                key={role._id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-xl border border-slate-200 p-5"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-brand-light flex-shrink-0">
                      <Shield className="h-5 w-5 text-brand" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-sm font-semibold text-slate-800">{role.name}</h3>
                        {role.isSystem && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 text-[10px] font-bold text-slate-500">
                            <Lock className="h-2.5 w-2.5" />
                            SYSTEM
                          </span>
                        )}
                      </div>
                      {role.description && (
                        <p className="text-xs text-slate-500 mt-0.5">{role.description}</p>
                      )}
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {role.permissions?.length > 0 ? (
                          role.permissions.map((perm: string) => {
                            const permInfo = PERMISSION_LABELS.find((p) => p.key === perm);
                            return (
                              <span
                                key={perm}
                                className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 text-[11px] font-medium"
                              >
                                {permInfo?.label || perm}
                              </span>
                            );
                          })
                        ) : (
                          <span className="text-xs text-slate-400">No permissions assigned</span>
                        )}
                      </div>
                    </div>
                  </div>

                  {!role.isSystem && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEdit(role)}
                        className="p-2 rounded-lg text-slate-400 hover:text-brand hover:bg-brand-light transition-colors"
                        title="Edit role"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      {confirmDelete === role._id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(role._id)}
                            disabled={deleting === role._id}
                            className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                            title="Confirm delete"
                          >
                            {deleting === role._id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => setConfirmDelete(null)}
                            className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 transition-colors"
                            title="Cancel"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDelete(role._id)}
                          className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
                          title="Delete role"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RoleManagementPage;
